import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch } from '@/api/axios-client'

/**
 * The signed-in person's own settings, saved on the server so they follow them across browsers.
 * Mirrors the backend's `/members/me/preferences` schema; unknown or unset keys are null.
 */
export interface Preferences {
  /** IANA zone, or null to follow the browser. */
  timezone: string | null
  /** Column keys last chosen for the Support report's Tickets CSV, in order. */
  supportExportColumns: string[] | null
  /** Last filters on the L3 Support Ticket queue; validated by the page that reads them. */
  supportQueueFilters: Record<string, string | boolean | string[]> | null
  /** Dynamic Island settings; null means the defaults. */
  /** What the collapsed sidebar does on hover; null = peek. */
  sidebar: { behavior?: 'peek' | 'dock'; magnification?: number } | null
  /** Focus mode and the Support L3 watch; null = off. */
  focusMode: FocusModeSetting | null
  dynamicIsland: Partial<{ enabled: boolean; weather: boolean; osNotifications: boolean; aiFacts: boolean; sounds: boolean; briefs: boolean; position: { x: number; y: number } | null }> | null
}

export interface FocusModeSetting {
  enabled: boolean
  /** Categories allowed through while focusing. */
  allow: string[]
  /** Also hold back Slack/WhatsApp for muted categories. */
  external: boolean
  /** ISO time focus turns itself off; null = until turned off. */
  until: string | null
  supportL3: {
    enabled: boolean
    teams: string[]
    priorities: string[]
    clientClasses: string[]
    /** 'me', 'unassigned' or member ids; empty = anyone. */
    assignedTo: string[]
    search: string
  }
}

const KEY = ['me-preferences'] as const

export function usePreferences() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiGet<Preferences>('members/me/preferences'),
    staleTime: 10 * 60_000,
    // A failed read must not block the app: defaults (browser zone, all columns) are fine.
    retry: 1,
  })
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<Preferences>) => apiPatch<Preferences>('members/me/preferences', patch),
    onSuccess: (next) => queryClient.setQueryData(KEY, next),
  })
}
